import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDescope, useSession } from '@descope/react-sdk';
import { Sidebar, MainContent } from 'components/organisms';
import { useAlert } from 'context/AlertContext';
import useAuthStore from 'stores/authStore';
import useChatStore from 'stores/chatStore';
import { HistoryProvider } from 'context/HistoryContext';
import { useChatHistorySync } from 'hooks/useChatHistorySync';

interface LocationState {
  uploadedFile?: File;
  message?: string;
  skipTariff?: boolean;
}

const LandingPageInner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sdk = useDescope();
  const { isAuthenticated } = useSession();
  const { showSuccess, showError } = useAlert();
  const { clearAuth, getUserId } = useAuthStore();
  const { loadConversation, sendMessage, reset, setSkipTariff } = useChatStore();

  const userId = getUserId();
  const hasUploadedRef = useRef<boolean>(false);

  // Sync chat store events with history refetching
  useChatHistorySync();

  // Handle file upload and message from navigation state (when redirected to /chat)
  useEffect(() => {
    const state = location.state as LocationState;
    const uploadedFile = state?.uploadedFile;
    const message = state?.message;
    const skipTariff = state?.skipTariff;

    if ((uploadedFile || message) && !hasUploadedRef.current) {
      hasUploadedRef.current = true;
      reset();
      setSkipTariff(skipTariff ?? true);
      sendMessage(message || '', uploadedFile, { skipTariff: skipTariff ?? true });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, sendMessage, navigate, location.pathname, reset, setSkipTariff]);

  const handleStartChat = (file: File | null = null, message?: string, options?: { skipTariff?: boolean }) => {
    navigate('/chat', { state: { uploadedFile: file, message, skipTariff: options?.skipTariff ?? true } });
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSignUp = () => {
    navigate('/signup');
  };

  const handleNewClassification = () => {
    // Reset any existing state and navigate to landing
    reset();
    hasUploadedRef.current = false;
    navigate('/');
  };

  const handleDocumentClick = async (documentId: string, conversationId?: string) => {
    console.log('[LandingPage] Conversation clicked:', { documentId, conversationId });

    // Navigate to the chat page
    navigate('/chat');

    try {
      // Use conversation_id if available, otherwise fallback to document_id
      const idToLoad = conversationId || documentId;
      console.log('[LandingPage] Loading conversation:', idToLoad);
      await loadConversation(idToLoad, userId!);
      showSuccess('Conversation loaded');
    } catch (error: any) {
      console.error('[LandingPage] Failed to load conversation:', error);
      showError('Failed to load conversation');
    }
  };

  const handleLogout = async () => {
    try {
      // Call Descope logout
      await sdk.logout();

      // Clear auth store
      clearAuth();

      // Show success message
      showSuccess('Successfully logged out');

      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      showError('Failed to logout. Please try again.');
    }
  };

  return (
    <div className="landing-page gradient-bg min-h-screen">
      <div className="gradient-content h-full">
        <Sidebar
          onNewClassification={handleNewClassification}
          onLogout={handleLogout}
          isAuthenticated={isAuthenticated}
          userId={userId}
          onDocumentClick={handleDocumentClick}
        />
        <MainContent
          onStartChat={handleStartChat}
          onLogin={handleLogin}
          onSignUp={handleSignUp}
          isAuthenticated={isAuthenticated}
        />
      </div>
    </div>
  );
};

const LandingPage: React.FC = () => {
  return (
    <HistoryProvider>
      <LandingPageInner />
    </HistoryProvider>
  );
};

export default LandingPage;
